// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — VariantPickerCombobox Component (FEAT-SALE-ITEMS-001)
// ─────────────────────────────────────────────────────────────────────────────
// Smart, high-performance variant search picker for fast retail checkout.
// Replaces legacy native <select> with a searchable, grouped, POS-style picker.
// ─────────────────────────────────────────────────────────────────────────────

import { Input } from '@/components/ui/FormControls';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Search, X, Plus, AlertCircle, Sparkles } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';

// §10 Edge Case: Cap visible results to prevent dropdown from becoming unwieldy
const MAX_VISIBLE_RESULTS = 50;

export interface CatalogVariant {
  id: string;
  productId: string;
  productName: string;
  productCode?: string;
  team?: string | null;
  sku: string;
  size: string;
  color?: string | null;
  defaultPrice: number;
  available: number;
}

interface VariantGroup {
  productId: string;
  productName: string;
  productCode?: string;
  team?: string | null;
  variants: CatalogVariant[];
}

export interface VariantPickerComboboxProps {
  variants: CatalogVariant[];
  onSelectVariant: (variant: CatalogVariant) => void;
  disabled?: boolean;
}

export default function VariantPickerCombobox({
  variants,
  onSelectVariant,
  disabled = false,
}: VariantPickerComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 200);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  // Sync container width for popover positioning
  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, [open]);

  // Filter variants based on debounced search
  // OPEN-004 resolved: On focus (before typing), show ALL in-stock variants
  const { filteredVariants, totalMatchCount } = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    let matches: CatalogVariant[];
    if (!term) {
      // OPEN-004: Show ALL in-stock variants on focus (no cap)
      const inStock = variants.filter((v) => v.available > 0);
      matches = inStock.length > 0 ? inStock : variants;
    } else {
      const tokens = term.split(/\s+/).filter(Boolean);
      matches = variants.filter((v) => {
        const targetString = `${v.productName} ${v.productCode ?? ''} ${v.team ?? ''} ${v.sku} ${v.size} ${v.color ?? ''}`.toLowerCase();
        return tokens.every((token) => targetString.includes(token));
      });
    }
    const total = matches.length;
    // §10 Edge Case: Cap at MAX_VISIBLE_RESULTS with overflow message
    return {
      filteredVariants: matches.length > MAX_VISIBLE_RESULTS ? matches.slice(0, MAX_VISIBLE_RESULTS) : matches,
      totalMatchCount: total,
    };
  }, [variants, debouncedSearch]);

  const isResultsCapped = totalMatchCount > MAX_VISIBLE_RESULTS;

  // §9 OPEN-007: Track no-results searches for catalog gap analysis
  useEffect(() => {
    const term = debouncedSearch.trim();
    if (term.length > 0 && totalMatchCount === 0) {
      console.info('[KLOTHIQ:variant_search_no_results]', { searchTerm: term, timestamp: new Date().toISOString() });
    }
  }, [debouncedSearch, totalMatchCount]);

  // Group filtered variants by product
  const groupedVariants = useMemo(() => {
    const groupsMap = new Map<string, VariantGroup>();
    for (const v of filteredVariants) {
      let group = groupsMap.get(v.productId);
      if (!group) {
        group = {
          productId: v.productId,
          productName: v.productName,
          productCode: v.productCode,
          team: v.team,
          variants: [],
        };
        groupsMap.set(v.productId, group);
      }
      group.variants.push(v);
    }
    return Array.from(groupsMap.values());
  }, [filteredVariants]);

  // Flatten selectable (in-stock) variants for keyboard navigation
  const selectableVariants = useMemo(() => {
    return filteredVariants.filter((v) => v.available > 0);
  }, [filteredVariants]);

  // Reset active keyboard index when search changes
  useEffect(() => {
    setActiveIndex(selectableVariants.length > 0 ? 0 : -1);
  }, [selectableVariants]);

  const handleSelect = useCallback(
    (variant: CatalogVariant) => {
      if (variant.available <= 0 || disabled) return;
      onSelectVariant(variant);
      setSearch('');
      setOpen(false);
      // Return focus to input for fast sequential additions
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    },
    [onSelectVariant, disabled]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }

    if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
      e.preventDefault();
      return;
    }

    if (selectableVariants.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < selectableVariants.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : selectableVariants.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < selectableVariants.length) {
        const item = selectableVariants[activeIndex];
        if (item) {
          handleSelect(item);
        }
      }
    }
  };

  const hasMatches = groupedVariants.length > 0;
  const isSearchEmpty = search.trim().length === 0;

  return (
    <div ref={containerRef} className="relative w-full">
      <Popover.Root open={open} onOpenChange={(o) => { if (!disabled) setOpen(o); }}>
        <Popover.Trigger asChild>
          <div className="relative flex items-center w-full">
            <Search className="absolute left-3.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label="Search and add products to sale"
              value={search}
              disabled={disabled}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => {
                if (!disabled) setOpen(true);
              }}
              onKeyDown={handleKeyDown}
              placeholder="🔍 Search products — type name, team, player, size, color, SKU..."
              className="flex h-11 w-full rounded-lg border border-border bg-background pl-10 pr-10 text-sm ring-offset-background transition-all placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 shadow-sm"
            />
            {search.length > 0 && !disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearch('');
                  inputRef.current?.focus();
                }}
                aria-label="Clear search"
                className="absolute right-3 rounded-full p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={6}
            onOpenAutoFocus={(e) => e.preventDefault()}
            collisionPadding={12}
            className="kiq-combo-content animate-in fade-in-0 zoom-in-95"
            style={{ width: containerWidth ? `${containerWidth}px` : 'var(--radix-popover-trigger-width)' }}
          >
            {/* Header hint */}
            <div className="flex items-center justify-between border-b border-border/60 bg-muted/30 px-3.5 py-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5 font-medium">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                {isSearchEmpty
                  ? 'All In-Stock Variants'
                  : isResultsCapped
                  ? `Showing ${MAX_VISIBLE_RESULTS} of ${totalMatchCount} — refine search`
                  : `Matching Variants (${filteredVariants.length})`}
              </span>
              <span className="text-[11px] opacity-70">
                Press <kbd className="rounded border bg-background px-1 py-0.5 font-mono text-[10px]">↓</kbd> <kbd className="rounded border bg-background px-1 py-0.5 font-mono text-[10px]">↑</kbd> then <kbd className="rounded border bg-background px-1 py-0.5 font-mono text-[10px]">Enter</kbd>
              </span>
            </div>

            {/* Scrollable list */}
            <div
              role="listbox"
              className="max-h-80 overflow-y-auto overflow-x-hidden p-2 space-y-3"
            >
              {!hasMatches ? (
                <div className="py-8 text-center">
                  <AlertCircle className="mx-auto h-8 w-8 text-muted-foreground/60 mb-2" />
                  <p className="text-sm font-medium text-foreground">No variants found</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    No results for &ldquo;{search}&rdquo;. Try another name, size, or SKU.
                  </p>
                </div>
              ) : (
                groupedVariants.map((group) => (
                  <div key={group.productId} className="space-y-1.5">
                    {/* Product Group Header */}
                    <div
                      role="presentation"
                      className="flex items-center justify-between rounded-md bg-muted/40 px-2.5 py-1.5 text-xs font-semibold text-teal-600 dark:text-teal-400 select-none"
                    >
                      <span className="min-w-0 break-words">
                        {group.variants[0]?.productCode && <span className="mr-2 inline-block max-w-full break-all align-bottom font-mono font-bold text-primary">{group.variants[0].productCode}</span>}
                        {group.productName}
                      </span>
                      {group.team && (
                        <span className="shrink-0 text-[11px] font-normal text-muted-foreground uppercase">
                          {group.team}
                        </span>
                      )}
                    </div>

                    {/* Variant items in this group */}
                    <div className="space-y-1 pl-1">
                      {group.variants.map((v) => {
                        const isOOS = v.available <= 0;
                        const selectableIdx = selectableVariants.findIndex((sv) => sv.id === v.id);
                        const isKeyboardActive = selectableIdx === activeIndex;

                        return (
                          <div
                            key={v.id}
                            role="option"
                            aria-selected={isKeyboardActive}
                            aria-disabled={isOOS}
                            onPointerDown={(e) => {
                              if (!isOOS) {
                                e.preventDefault();
                              }
                            }}
                            onClick={() => handleSelect(v)}
                            className={`group relative flex flex-wrap items-center justify-between gap-y-1.5 rounded-lg px-3 py-2 min-h-[44px] text-sm select-none transition-colors ${
                              isOOS
                                ? 'opacity-40 cursor-not-allowed bg-muted/10'
                                : isKeyboardActive
                                ? 'bg-accent text-accent-foreground cursor-pointer ring-1 ring-primary/40'
                                : 'cursor-pointer hover:bg-accent/80 hover:text-accent-foreground'
                            }`}
                          >
                            {/* Left: Size Chip + Color + SKU */}
                            <div className="flex items-center gap-2.5 min-w-0 pr-3">
                              <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-1.5 rounded bg-secondary text-secondary-foreground text-xs font-bold shrink-0">
                                {v.size}
                              </span>
                              <div className="flex flex-col min-w-0">
                                <span className="text-sm font-medium text-foreground break-words">
                                  {v.color || 'Standard'}
                                </span>
                                <span className="text-[11px] text-muted-foreground font-mono break-all">
                                  {v.sku}
                                </span>
                              </div>
                            </div>

                            {/* Right: Price + Stock Badge + Add Action */}
                            <div className="flex items-center gap-3 shrink-0">
                              <span className="text-sm font-semibold text-foreground">
                                ₹{v.defaultPrice.toFixed(2)}
                              </span>

                              {isOOS ? (
                                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                                  OUT OF STOCK
                                </span>
                              ) : (
                                <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                                  {v.available} in stock
                                </span>
                              )}

                              {!isOOS && (
                                <span className="flex items-center gap-0.5 rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                  <Plus className="h-3 w-3" /> Add
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
